/**
 * Central query-key factory. Every key is built here so invalidation is precise and
 * discoverable — a stringly-typed key scattered through components cannot be invalidated
 * reliably.
 */
export const queryKeys = {
  platform: {
    health: () => ['platform', 'health'] as const,
    meta: () => ['platform', 'meta'] as const,
  },
  auth: {
    session: () => ['auth', 'session'] as const,
  },
  /** A property's or a space's photos and video, as one workspace sees them (2026-10-27). */
  media: (workspace: string, kind: 'property' | 'space', id: number | string) =>
    ['media', workspace, kind, String(id)] as const,
  /** Reference data shared by every workspace. Keyed by kind so one list can refresh alone. */
  choices: (kind: string) => ['choices', kind] as const,
  /** Per state: the list differs for each, and Niger's is 4,887 long. */
  cities: (state: string) => ['choices', 'cities', state] as const,
  landlord: {
    /** Everything under one root, so signing out invalidates precisely. */
    all: () => ['landlord'] as const,
    overview: () => ['landlord', 'overview'] as const,
    profile: () => ['landlord', 'profile'] as const,
    /** One entry per candidate: a name already checked this session is answered from cache. */
    usernameCheck: (username: string) => ['landlord', 'profile', 'username', username] as const,
    properties: (filters: Record<string, unknown>) => ['landlord', 'properties', filters] as const,
    /** The property list's city filter options. Not filtered itself — it IS the filter. */
    propertyCities: () => ['landlord', 'properties', 'cities'] as const,
    property: (id: number | string) => ['landlord', 'property', String(id)] as const,
    propertySpaces: (id: number | string, filters: Record<string, unknown>) =>
      ['landlord', 'property', String(id), 'spaces', filters] as const,
    serviceCharges: (id: number | string) =>
      ['landlord', 'property', String(id), 'service-charges'] as const,
    spaces: (filters: Record<string, unknown>) => ['landlord', 'spaces', filters] as const,
    /** The space filter bar's options and bounds. Not filtered itself — it IS the filter. */
    spaceFilters: () => ['landlord', 'spaces', 'filters'] as const,
    space: (id: number | string) => ['landlord', 'space', String(id)] as const,
    tenants: (filters: Record<string, unknown>) => ['landlord', 'tenants', filters] as const,
    tenant: (id: number | string) => ['landlord', 'tenant', String(id)] as const,
    estates: (filters: Record<string, unknown>) => ['landlord', 'estates', filters] as const,
    estate: (id: number | string) => ['landlord', 'estate', String(id)] as const,
  },
  /**
   * The onboarding wizard (Phase 05). Its own root rather than a branch of `landlord`, because
   * the same keys serve the affiliate workspace's copy of the wizard in Phase 06.
   */
  onboarding: {
    all: () => ['onboarding'] as const,
    templates: () => ['onboarding', 'templates'] as const,
    sections: (propertyId: number | string) =>
      ['onboarding', 'sections', String(propertyId)] as const,
    blueprints: (propertyId: number | string) =>
      ['onboarding', 'blueprints', String(propertyId)] as const,
    blueprint: (id: string) => ['onboarding', 'blueprint', id] as const,
    preview: (id: string) => ['onboarding', 'preview', id] as const,
    /** The stateless dry-run, keyed by the hash of the exact body it was asked about. */
    dryRun: (workspace: string, scope: string, hash: string) =>
      ['onboarding', 'dry-run', workspace, scope, hash] as const,
    /** Engine v3: one space's explanation, keyed by the body hash and the space key. */
    explain: (workspace: string, scope: string, hash: string, spaceKey: string) =>
      ['onboarding', 'explain', workspace, scope, hash, spaceKey] as const,
    /** Engine v3: a candidate rule's impact, keyed by the body hash and the candidate's hash. */
    ruleImpact: (workspace: string, scope: string, hash: string, candidate: string) =>
      ['onboarding', 'rule-impact', workspace, scope, hash, candidate] as const,
    task: (id: string) => ['onboarding', 'task', id] as const,
  },
  /**
   * Tenancies, renewals and payments (Phase 07). Keyed by workspace first: the same pages serve
   * `/app/landlord/*` and `/app/affiliate/*`, and an officer who is also a landlord must never
   * see one workspace's cached rows under the other.
   */
  tenancy: {
    all: () => ['tenancy'] as const,
    list: (ws: string, filters: Record<string, unknown>) =>
      ['tenancy', ws, 'list', filters] as const,
    /** The tenant list's filter options. Not filtered itself — it IS the filter. */
    filters: (ws: string, params: Record<string, unknown> = {}) =>
      ['tenancy', ws, 'list', 'filters', params] as const,
    detail: (ws: string, id: number | string) => ['tenancy', ws, 'detail', String(id)] as const,
    sessions: (ws: string, id: number | string) => ['tenancy', ws, 'sessions', String(id)] as const,
    quote: (ws: string, id: number | string, units: number) =>
      ['tenancy', ws, 'quote', String(id), units] as const,
    board: (ws: string, filters: Record<string, unknown>) =>
      ['tenancy', ws, 'board', filters] as const,
    /** The payment list's filter options. Not filtered itself — it IS the filter. */
    paymentFilters: (ws: string) => ['tenancy', ws, 'payments', 'filters'] as const,
    payments: (ws: string, filters: Record<string, unknown>) =>
      ['tenancy', ws, 'payments', filters] as const,
    payment: (ws: string, id: string) => ['tenancy', ws, 'payment', id] as const,
  },
  /**
   * Affiliate commission (Phase 10). Its own root rather than a branch of `affiliate`,
   * because the administrator's console reads the same domain from a different prefix and the
   * two must never share a cache entry — an admin who is also an officer would otherwise see
   * one workspace's rows under the other.
   */
  commission: {
    all: () => ['commission'] as const,
    summary: () => ['commission', 'summary'] as const,
    /** Filters, not a cursor: cursor pages live inside one infinite query under this key. */
    entries: (scope: string, filters: Record<string, unknown>) =>
      ['commission', scope, 'entries', filters] as const,
    entry: (scope: string, id: string) => ['commission', scope, 'entry', id] as const,
    rules: (scope: string, filters: Record<string, unknown> = {}) =>
      ['commission', scope, 'rules', filters] as const,
    rule: (id: string) => ['commission', 'rule', id] as const,
    payouts: (scope: string, filters: Record<string, unknown> = {}) =>
      ['commission', scope, 'payouts', filters] as const,
    payout: (scope: string, id: string) => ['commission', scope, 'payout', id] as const,
    payoutPreview: (params: Record<string, unknown>) =>
      ['commission', 'payout-preview', params] as const,
    adjustments: (scope: string, filters: Record<string, unknown> = {}) =>
      ['commission', scope, 'adjustments', filters] as const,
    unmatched: (filters: Record<string, unknown> = {}) =>
      ['commission', 'unmatched', filters] as const,
  },
  affiliate: {
    /** Everything under one root, so signing out or switching officer invalidates precisely. */
    all: () => ['affiliate'] as const,
    me: () => ['affiliate', 'me'] as const,
    /** Students' space requests near the officer's campuses (2026-10-10). */
    studentDemand: () => ['affiliate', 'student-demand'] as const,
    studentRequests: (filters: Record<string, unknown>) =>
      ['affiliate', 'student-requests', filters] as const,
    studentRequest: (reference: string) => ['affiliate', 'student-request', reference] as const,
    /** The thread with the student, for their handler (2026-10-12). */
    studentRequestMessages: (reference: string) =>
      ['affiliate', 'student-request', reference, 'messages'] as const,
    /** Roommate requests near the officer's campuses, which officers handle too (2026-10-26). */
    roommateRequests: (filters: Record<string, unknown>) =>
      ['affiliate', 'roommate-requests', filters] as const,
    roommateRequest: (reference: string) => ['affiliate', 'roommate-request', reference] as const,
    roommateRequestMessages: (reference: string) =>
      ['affiliate', 'roommate-request', reference, 'messages'] as const,
    /** The escorted group viewings the officer runs (2026-10-12). */
    viewings: (filters: Record<string, unknown>) => ['affiliate', 'viewings', filters] as const,
    viewing: (id: string) => ['affiliate', 'viewing', id] as const,
    landlords: (filters: Record<string, unknown>) => ['affiliate', 'landlords', filters] as const,
    landlord: (id: number | string) => ['affiliate', 'landlord', String(id)] as const,
    properties: (filters: Record<string, unknown>) => ['affiliate', 'properties', filters] as const,
    propertyCities: () => ['affiliate', 'properties', 'cities'] as const,
    property: (id: number | string) => ['affiliate', 'property', String(id)] as const,
    /** Filters, not a cursor: cursor pages live inside one infinite query under this key. */
    activity: (filters: Record<string, unknown> = {}) =>
      ['affiliate', 'activity', filters] as const,
    // Phase 06.
    space: (id: number | string) => ['affiliate', 'space', String(id)] as const,
    notes: (landlordId: number | string) => ['affiliate', 'notes', String(landlordId)] as const,
    followUps: (bucket = 'due') => ['affiliate', 'follow-ups', bucket] as const,
    /** A supervisor's register of the other officers (2026-10-11). */
    officers: (filters: Record<string, unknown>) => ['affiliate', 'officers', filters] as const,
    officerApplications: (params: Record<string, unknown>) =>
      ['affiliate', 'officer-applications', params] as const,
    officer: (id: string) => ['affiliate', 'officer', id] as const,
    officerAudit: (id: string) => ['affiliate', 'officer', id, 'audit'] as const,
  },
  /**
   * The public marketplace (Phase 08). Its own root, deliberately outside every workspace:
   * these responses are identical for everybody, so signing in or out must NOT invalidate
   * them — `queryClient.clear()` on sign-out would otherwise throw away a visitor's browse.
   */
  marketplace: {
    all: () => ['marketplace'] as const,
    listings: (filters: Record<string, unknown>) => ['marketplace', 'listings', filters] as const,
    listing: (slug: string) => ['marketplace', 'listing', slug] as const,
    similar: (slug: string) => ['marketplace', 'similar', slug] as const,
    properties: (filters: Record<string, unknown>) =>
      ['marketplace', 'properties', filters] as const,
    property: (slug: string) => ['marketplace', 'property', slug] as const,
    facets: (filters: Record<string, unknown>) => ['marketplace', 'facets', filters] as const,
    locations: () => ['marketplace', 'locations'] as const,
    featured: () => ['marketplace', 'featured'] as const,
    showcase: () => ['marketplace', 'showcase'] as const,
    suggest: (term: string) => ['marketplace', 'suggest', term] as const,
    contact: () => ['marketplace', 'contact'] as const,
    /** A campus's space-request form: configuration, the same for every visitor. */
    spaceRequestCampus: (campus: string) => ['marketplace', 'space-request', campus] as const,
    roommateProposal: (token: string) => ['marketplace', 'roommate-proposal', token] as const,
    /** A requester following their request by its signed link (2026-10-12). */
    spaceRequestTrack: (token: string) => ['marketplace', 'space-request-track', token] as const,
    /** A roommate request's own page, by its signed link (2026-10-26). */
    roommateTrack: (token: string) => ['marketplace', 'roommate-track', token] as const,
  },
  /**
   * Analytics (Phase 11). Keyed by workspace first, then report, then the whole filter object —
   * the range, granularity, comparison and filters all change the answer, so they all belong in
   * the key. They are also the URL, which is what makes a shared link and a cache hit the same
   * thing.
   */
  insights: {
    all: () => ['insights'] as const,
    report: (ws: string, name: string, params: Record<string, unknown>) =>
      ['insights', ws, 'report', name, params] as const,
    leaderboard: (ws: string, params: Record<string, unknown>) =>
      ['insights', ws, 'leaderboard', params] as const,
    performance: (ws: string, params: Record<string, unknown>) =>
      ['insights', ws, 'performance', params] as const,
    activity: (ws: string, params: Record<string, unknown>) =>
      ['insights', ws, 'activity', params] as const,
  },
  /**
   * The administration console (Phase 12). One root, so signing out or switching workspace
   * invalidates every admin read at once; every key carries the filters that change the answer.
   */
  admin: {
    all: () => ['admin'] as const,
    affiliates: (filters: Record<string, unknown>) => ['admin', 'affiliates', filters] as const,
    affiliateApplications: (params: Record<string, unknown>) =>
      ['admin', 'affiliate-applications', params] as const,
    affiliate: (id: string) => ['admin', 'affiliate', id] as const,
    affiliateAudit: (id: string) => ['admin', 'affiliate', id, 'audit'] as const,
    users: (role: string, filters: Record<string, unknown>) =>
      ['admin', 'users', role, filters] as const,
    user: (id: string) => ['admin', 'user', id] as const,
    properties: (filters: Record<string, unknown>) => ['admin', 'properties', filters] as const,
    propertyFacets: () => ['admin', 'properties', 'facets'] as const,
    property: (id: string) => ['admin', 'property', id] as const,
    propertyForm: (id: number | string) => ['admin', 'property', String(id), 'form'] as const,
    /** A searching picker over one register (`landlords`, `officers`): its results for a term,
     *  or, with `selected`, the one row a shared link already chose. */
    picker: (register: string, term: string, selected = '') =>
      ['admin', 'picker', register, term, selected] as const,
    payments: (filters: Record<string, unknown>) => ['admin', 'payments', filters] as const,
    payment: (id: string) => ['admin', 'payment', id] as const,
    audit: (filters: Record<string, unknown>) => ['admin', 'audit', filters] as const,
    auditVocabulary: () => ['admin', 'audit', 'vocabulary'] as const,
    integrity: (filters: Record<string, unknown>) => ['admin', 'integrity', filters] as const,
    anomalies: (filters: Record<string, unknown>) => ['admin', 'anomalies', filters] as const,
    health: () => ['admin', 'health'] as const,
    flags: (user: string) => ['admin', 'flags', user] as const,
    webhooks: (filters: Record<string, unknown>) => ['admin', 'webhooks', filters] as const,
    choices: () => ['admin', 'settings', 'choices'] as const,
    contacts: () => ['admin', 'settings', 'contacts'] as const,
    broadcasts: () => ['admin', 'settings', 'broadcasts'] as const,
    spaceRequests: (filters: Record<string, unknown>) =>
      ['admin', 'space-requests', filters] as const,
    spaceRequestFacets: () => ['admin', 'space-requests', 'facets'] as const,
    spaceRequest: (id: string) => ['admin', 'space-request', id] as const,
    spaceRequestMessages: (id: string) => ['admin', 'space-request', id, 'messages'] as const,
    /** Roommate requests (2026-10-22), beside the space requests. */
    roommateRequests: (filters: Record<string, unknown>) =>
      ['admin', 'roommate-requests', filters] as const,
    roommateRequest: (id: string) => ['admin', 'roommate-request', id] as const,
    roommateRequestMessages: (id: string) => ['admin', 'roommate-request', id, 'messages'] as const,
    /** APMP on WhatsApp (2026-10-12): one prefix, so a save refreshes all of it. */
    whatsapp: () => ['admin', 'whatsapp'] as const,
    whatsappSetup: () => ['admin', 'whatsapp', 'setup'] as const,
    whatsappDocuments: () => ['admin', 'whatsapp', 'documents'] as const,
    whatsappConversations: (filters: Record<string, unknown>) =>
      ['admin', 'whatsapp', 'conversations', filters] as const,
    whatsappMessages: (id: string, filters: Record<string, unknown>) =>
      ['admin', 'whatsapp', 'messages', id, filters] as const,
    /** Support officers (2026-10-12): the register and one officer. */
    supportOfficers: (filters: Record<string, unknown>) =>
      ['admin', 'support-officers', filters] as const,
    supportOfficer: (id: string) => ['admin', 'support-officer', id] as const,
    /** Affiliate institutions (2026-10-22): the register, one institution, the campuses. */
    institutions: (filters: Record<string, unknown>) => ['admin', 'institutions', filters] as const,
    institution: (id: string) => ['admin', 'institution', id] as const,
    institutionCampuses: () => ['admin', 'institutions', 'campuses'] as const,
    /** An officer's institutions and whether they do general work. */
    officerInstitutions: (id: string) => ['admin', 'officer-institutions', id] as const,
  },
  /**
   * An affiliate institution's workspace (2026-10-22), on the Support workspace's shape: one
   * root; a register's key is its name and filters, a record's its name and id.
   */
  institution: {
    all: () => ['institution'] as const,
    me: () => ['institution', 'me'] as const,
    overview: () => ['institution', 'overview'] as const,
    list: (name: string, filters: Record<string, unknown>) =>
      ['institution', name, filters] as const,
    detail: (name: string, id: string) => ['institution', name, 'one', id] as const,
  },
  /**
   * The Support workspace (2026-10-12). One root; a register's key is its name and filters, a
   * record's its name and id, so one prefix refreshes a whole register.
   */
  support: {
    all: () => ['support'] as const,
    me: () => ['support', 'me'] as const,
    overview: () => ['support', 'overview'] as const,
    list: (name: string, filters: Record<string, unknown>) => ['support', name, filters] as const,
    detail: (name: string, id: string) => ['support', name, 'one', id] as const,
    messages: (reference: string) => ['support', 'messages', reference] as const,
  },
  /** The notification centre. Self-scoped, so nothing but the prefix distinguishes them. */
  notifications: {
    all: () => ['notifications'] as const,
    summary: (ws: string) => ['notifications', ws, 'summary'] as const,
    list: (ws: string, filters: Record<string, unknown>) =>
      ['notifications', ws, 'list', filters] as const,
  },
} as const

/**
 * staleTime policy (architecture.md §4). Money is ALWAYS 0 — a cached balance shown as
 * current is a correctness bug, not a performance win.
 */
export const staleTimes = {
  static: 60 * 60 * 1000,
  reference: 10 * 60 * 1000,
  list: 30 * 1000,
  detail: 60 * 1000,
  money: 0,
} as const
